import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Logger } from "@/lib/logger/Logger";
import { useCentralStore } from "../../store/Central";
import { supabase, setRateLimitBypass } from "@/lib/supabase";
import clsx from "clsx";
import {
	showNotification,
	hasFullFeatureSet,
} from "../../store/Central/selectors";
import {
	FileEntityType,
	SupabaseWriteTableEnum,
} from "@/lib/supabase/supabaseTypes";
import { useFileUpload } from "../../hooks/useFileUpload";
import {
	Card,
	CardContent,
	CardHeader,
	CardTitle,
} from "../../components/ui/card";
import { Progress } from "../../components/ui/progress";
import { Badge } from "../../components/ui/badge";
import { Checkbox } from "../../components/ui/checkbox";
import { Label } from "../../components/ui/label";
import { Button } from "../../components/ui/button";
import {
	Accordion,
	AccordionContent,
	AccordionItem,
	AccordionTrigger,
} from "../../components/ui/accordion";
import {
	Upload,
	CheckCircle2,
	XCircle,
	AlertCircle,
	Image as ImageIcon,
	ChevronRight,
	ArrowDown,
	Info,
} from "lucide-react";
import {
	MIGRATION_DECISION,
	ONBOARDING_STATUS,
	ONBOARDING_STEPS,
} from "../../constants/onboarding";
import { PdfTemplateType } from "../../pdf-templates/document-template.types";
import {
	FIRST_FEE_STAGE,
	ReminderTemplateType,
} from "../debitoren/mahnwesen/utils/reminder-stage";
import { useAttributesStore } from "../../store/Attributes";
import { applyRounding } from "../../lib/utils/calculate";

const formatDate = (dateString: string): string | undefined => {
	if (!dateString) return undefined;
	const [date, time] = dateString.trim().split(" ");
	const parts = date.split(".");
	if (parts.length !== 3) return undefined;
	const [day, month, year] = parts;
	return `${year}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`;
};

// Parse a DLXP timestamp like "26.03.2024 06:34:42:000" into ISO
const parseDlxpTimestamp = (s: string | undefined): string | undefined => {
	if (!s) return undefined;
	const m = s.match(/^(\d{2})\.(\d{2})\.(\d{4})\s+(\d{2}):(\d{2}):(\d{2})/);
	if (!m) return undefined;
	const [, d, mo, y, h, mi, se] = m;
	return `${y}-${mo}-${d}T${h}:${mi}:${se}.000Z`;
};

// Parse the legacy DLXP IMAGE.txt file into a map keyed by lowercase
// braces-stripped Bild filename (e.g. "002d2b24-...JPG"), so we can look up
// each binary's metadata when processing image uploads.
const parseImageTxt = (
	content: string
): Map<string, Record<string, string>> => {
	const map = new Map<string, Record<string, string>>();
	if (!content) return map;
	const lines = content.split("\n");
	if (lines.length === 0) return map;
	const columns = lines[0].split("|").map((c) => c.trim().replace(/\r$/, ""));
	for (let i = 1; i < lines.length; i++) {
		const line = lines[i];
		if (!line.trim().startsWith("{")) continue;
		const values = line.split("|");
		if (values.length !== columns.length) continue;
		const row: Record<string, string> = {};
		for (let c = 0; c < columns.length; c++) {
			row[columns[c]] = values[c].trim();
		}
		const bild = row["Bild"];
		if (!bild) continue;
		map.set(bild.replace(/[{}]/g, "").toLowerCase(), row);
	}
	return map;
};

// TODO:
// Konfiguration.XML
// Doc
// Pic
// RTF

/**
 * We are ignoring the following files:
 * - Tickets.txt
 * - PLZ.txt
 * - PINK.txt
 */
const FILES_TO_PROCESS = [
	"Labors.txt", // Ignore
	"TAJournal.txt", // Ignore
	"Mitarbeiter.txt", // Ignore
	"Garanten.txt",
	"Kunden.txt",
	"Archiv.txt",
	"LListe.txt", // Ignore
	"Gruppen.txt", // Ignore
	"Artikel.txt",
	"Tarif.txt",
	"Bloecke.txt",
	"BPositionen.txt",
	"Debitoren.txt",
	"Faelle.txt",
	"FPositionen.txt",
	// IMAGE.txt is handled separately — parsed locally and used to drive
	// per-image storage upload + public.files insert (see processImageFiles)
	"Ressourcen.txt", // Ignore
	"Termine.txt", // Ignore
	"Aufgaben.txt", // Ignore
	"Pendenzen.txt", // Ignore
	"Farbkarten.txt", // Ignore
	"Vorlagen.txt", // Ignore
	"Layouts.txt",
	"SpotNotes.txt", // Ignore for now
	"EMails.txt", // Ignore
].sort();

const EXPECTED_FILES = [
	"Artikel.txt",
	"BPositionen.txt",
	"Bloecke.txt",
	"FPositionen.txt",
	"Faelle.txt",
	"Garanten.txt",
	"Kunden.txt",
	"Tarif.txt",
];

interface ProgressInterface {
	[name: string]: {
		totalLength: number;
		processedSuccess: number;
		processedError: number;
	};
}

const DLXP_REMINDER_TITLE_KEYS: Record<string, ReminderTemplateType[]> = {
	TitelZahlungserinnerung_D: [PdfTemplateType.ZAHLUNGSERINNERUNG],
	TitelMahnung1_D: [PdfTemplateType.ERSTE_MAHNUNG],
	TitelMahnung2_D: [
		PdfTemplateType.FOLGEMAHNUNG,
		PdfTemplateType.LETZTE_MAHNUNG,
	],
};
export const MigrationPage: React.FC = () => {
	const navigate = useNavigate();
	const organizationId = useCentralStore((state) => state.organization?.id);
	const organization = useCentralStore((state) => state.organization);
	const subscriptionTier = useCentralStore((state) => state.subscriptionTier);
	const uploadStatus = useCentralStore((state) => state.uploadStatus);
	const setUploadStatus = useCentralStore((state) => state.setUploadStatus);
	const setOnboardingAttribute = useAttributesStore(
		(state) => state.setOnboardingAttribute
	);
	const migrationDecision = useAttributesStore((state) =>
		state.getOnboardingAttribute("onboarding_migration_decision")
	);
	const [progress, setProgress] = useState<ProgressInterface>(
		Object.fromEntries(
			FILES_TO_PROCESS.map((name) => [
				name,
				{ totalLength: 0, processedSuccess: 0, processedError: 0 },
			])
		)
	);
	const [rowErrors, setRowErrors] = useState<{
		[name: string]: any[];
	}>({});
	const [filesFound, setFilesFound] = useState<string[]>([]);
	const [includeImages, setIncludeImages] = useState(false);
	const [xmlProcessed, setXmlProcessed] = useState(false);
	const [xmlError, setXmlError] = useState<string | null>(null);
	const [imageStats, setImageStats] = useState({
		total: 0,
		uploaded: 0,
		failed: 0,
	});
	const [openAccordion, setOpenAccordion] = useState<string[]>([]);
	const [filesInitialized, setFilesInitialized] = useState<Set<string>>(
		new Set()
	);

	// Prevent page reload/leave during upload
	useEffect(() => {
		const handleBeforeUnload = (e: BeforeUnloadEvent) => {
			if (uploadStatus === "uploading") {
				e.preventDefault();
				return "";
			}
		};

		window.addEventListener("beforeunload", handleBeforeUnload);
		return () =>
			window.removeEventListener("beforeunload", handleBeforeUnload);
	}, [uploadStatus]);

	// Check completion status when progress updates
	useEffect(() => {
		if (
			uploadStatus === "uploading" &&
			(filesFound.length > 0 || imageStats.total > 0)
		) {
			checkAllFilesComplete();
		} else if (uploadStatus === "complete") {
			setOnboardingAttribute(
				ONBOARDING_STEPS.MIGRATION_DECISION,
				MIGRATION_DECISION.MIGRATION_DONE,
				"org"
			);
		}
	}, [
		progress,
		filesInitialized,
		uploadStatus,
		filesFound,
		imageStats,
		setOnboardingAttribute,
	]);

	const handleSkipMigration = async () => {
		await setOnboardingAttribute(
			ONBOARDING_STEPS.MIGRATION_DECISION,
			MIGRATION_DECISION.NO_MIGRATION,
			"org"
		);
		navigate("/");
	};

	const checkAllFilesComplete = () => {
		// Nothing to check yet — neither txt batches nor image batch has started
		if (filesFound.length === 0 && imageStats.total === 0) {
			return;
		}

		// txt files (skipped entirely if user dropped only the Pic folder)
		const txtComplete =
			filesFound.length === 0 ||
			(filesInitialized.size > 0 &&
				filesFound.every((fileName) => {
					const fileProgress = progress[fileName];
					if (!fileProgress) return false;
					if (!filesInitialized.has(fileName)) return false;
					if (fileProgress.totalLength === 0) return true;
					const processed =
						fileProgress.processedSuccess +
						fileProgress.processedError;
					return processed === fileProgress.totalLength;
				}));

		const imagesComplete =
			imageStats.total === 0 ||
			imageStats.uploaded + imageStats.failed === imageStats.total;

		if (txtComplete && imagesComplete) {
			const hasErrors =
				filesFound.some(
					(fileName) => progress[fileName]?.processedError > 0
				) || imageStats.failed > 0;
			setUploadStatus(hasErrors ? "error" : "complete");
		}
	};

	const readTxtFile = async (
		fileData: string,
		fileName: string,
		versionId: number
	) => {
		if (!organizationId) {
			Logger.error("[MigrationPage] Organization ID not found.");
			return;
		}

		const columns = fileData
			.split("\n")[0]
			.split("|")
			.map((col) => col.trim().replace(/\r$/, "")); // The last column can contain a \r, which we need to remove
		const columnCount = columns.length;

		const rows = fileData
			.split("\n")
			.filter((row) => row.trim().startsWith("{"))
			.map((row) => {
				const rowData = row.split("|");
				if (rowData.length !== columnCount) {
					return null;
				}
				const rowDict = Object.fromEntries(
					columns.map((column, index) => [
						column,
						rowData[index].trim(),
					])
				);
				return {
					table_name: fileName,
					row_value: rowDict,
					organization_id: organizationId,
					row_id: rowData[0],
					e_datum: formatDate(rowData[1]),
					version: versionId,
				};
			})
			.filter((row) => row !== null);

		// Mark this file as initialized
		setFilesInitialized((prev) => new Set(prev).add(fileName));

		setProgress((prev) => ({
			...prev,
			[fileName]: {
				...prev[fileName],
				totalLength: rows.length,
			},
		}));

		const processBatch = async (batch: any) => {
			Logger.info(
				`[MigrationPage] Processing batch for file ${fileName}`,
				batch
			);
			const { data, error } = await supabase.from("DL_MIG").insert(batch);

			if (error) {
				Logger.error(
					error,
					{},
					`[MigrationPage] Error processing batch for file ${fileName}`
				);
				return false;
			}
			Logger.info(
				`[MigrationPage] Batch for file ${fileName} processed successfully.`,
				data
			);
			return true;
		};

		const batchSize = 191;
		let errors: any[] = [];
		for (let i = 0; i < rows.length; i += batchSize) {
			const batch = rows.slice(i, i + batchSize);
			const success = await processBatch(batch);
			if (!success) {
				errors = errors.concat(batch);
			} else {
				setProgress((prev) => ({
					...prev,
					[fileName]: {
						...prev[fileName],
						processedSuccess:
							prev[fileName].processedSuccess + batch.length,
					},
				}));
			}

			// wait 300ms
			await new Promise((resolve) => setTimeout(resolve, 300));
		}

		const retryBatchSize = 11;
		let errors2: any[] = [];
		for (let i = 0; i < errors.length; i += retryBatchSize) {
			const batch = errors.slice(i, i + retryBatchSize);
			const success = await processBatch(batch);
			if (!success) {
				errors2 = errors2.concat(batch);
			} else {
				setProgress((prev) => ({
					...prev,
					[fileName]: {
						...prev[fileName],
						processedSuccess:
							prev[fileName].processedSuccess + batch.length,
					},
				}));
			}

			// wait 300ms
			await new Promise((resolve) => setTimeout(resolve, 300));
		}

		const retryBatchSize2 = 1;
		let remainingErrors: any[] = [];
		for (let i = 0; i < errors2.length; i += retryBatchSize2) {
			const batch = errors2.slice(i, i + retryBatchSize2);
			const success = await processBatch(batch);
			if (!success) {
				Logger.error(
					`[MigrationPage] Error processing batch for file ${fileName}`
				);
				setProgress((prev) => ({
					...prev,
					[fileName]: {
						...prev[fileName],
						processedError:
							prev[fileName].processedError + batch.length,
					},
				}));
				remainingErrors = remainingErrors.concat(batch);
			} else {
				setProgress((prev) => ({
					...prev,
					[fileName]: {
						...prev[fileName],
						processedSuccess:
							prev[fileName].processedSuccess + batch.length,
					},
				}));
			}

			// wait 300ms
			await new Promise((resolve) => setTimeout(resolve, 300));
		}
		if (remainingErrors.length > 0) {
			Logger.error(
				"[MigrationPage] Error processing batch for file",
				{},
				remainingErrors,
				`${fileName}`
			);
			setRowErrors((prev) => ({
				...prev,
				[fileName]: remainingErrors,
			}));
		}
	};

	const handleReset = () => {
		setUploadStatus("idle");
		setProgress(
			Object.fromEntries(
				FILES_TO_PROCESS.map((name) => [
					name,
					{ totalLength: 0, processedSuccess: 0, processedError: 0 },
				])
			)
		);
		setRowErrors({});
		setFilesFound([]);
		setFilesInitialized(new Set());
		setIncludeImages(false);
		setXmlProcessed(false);
		setXmlError(null);
		setImageStats({ total: 0, uploaded: 0, failed: 0 });
	};

	const handleFolderUpload = async (files: File[]) => {
		if (!files || files.length === 0) {
			showNotification({
				message: "Bitte wählen Sie einen Ordner aus.",
				type: "error",
			});
			return;
		}

		// Extract basename from file (handles both direct files and nested paths)
		const getBasename = (file: File): string => {
			const path = (file as any).webkitRelativePath || file.name;
			return path.split("/").pop() || "";
		};

		// Filter files to only process the ones in FILES_TO_PROCESS
		const relevantTxtFiles = files.filter((file) => {
			const basename = getBasename(file);
			return FILES_TO_PROCESS.includes(basename);
		});

		// Find XML file (always process if present)
		const xmlFile = files.find((file) => {
			const basename = getBasename(file);
			return basename.toLowerCase() === "konfiguration.xml";
		});

		// Find image files — match by extension because browsers often leave
		// file.type empty for files coming out of webkitdirectory / drag-drop folders
		const imageFiles = includeImages
			? files.filter((file) => {
					const path = (file as any).webkitRelativePath || file.name;
					return /\.(jpe?g|png|gif|tiff?|bmp|webp|heic)$/i.test(path);
				})
			: [];

		// IMAGE.txt is the legacy metadata file that links each image binary to a
		// job (via IMA_FAEPt → jobs.fae_id). It's parsed locally and used to drive
		// the public.files inserts in processImageFiles. Not pushed to DL_MIG.
		const imageTxtFile =
			imageFiles.length > 0
				? files.find(
						(f) => getBasename(f).toLowerCase() === "image.txt"
					)
				: undefined;

		if (
			relevantTxtFiles.length === 0 &&
			!xmlFile &&
			imageFiles.length === 0
		) {
			showNotification({
				message: "Keine relevanten Dateien im Ordner gefunden.",
				type: "error",
			});
			return;
		}

		setFilesFound(relevantTxtFiles.map((f) => getBasename(f)));

		Logger.info(
			`[MigrationPage] Found ${relevantTxtFiles.length} TXT files, ${
				xmlFile ? 1 : 0
			} XML files, ${imageFiles.length} image files`
		);

		// Create MIG_VER entry for this migration batch
		if (!organizationId) {
			showNotification({
				message: "Organisation nicht gefunden.",
				type: "error",
			});
			return;
		}

		const { data: migVerData, error: migVerError } = await supabase
			.from("MIG_VER")
			.insert({
				organization_id: organizationId,
			})
			.select("id")
			.single();

		if (migVerError || !migVerData) {
			Logger.error(
				"[MigrationPage] Failed to create MIG_VER entry" + migVerError
			);
			showNotification({
				message: "Fehler beim Erstellen der Migrations-Version.",
				type: "error",
			});
			return;
		}

		const versionId = migVerData.id;
		Logger.info(
			`[MigrationPage] Created MIG_VER entry with ID: ${versionId}`
		);

		// Reset state for new upload
		setFilesInitialized(new Set());
		setUploadStatus("uploading");

		// Process TXT files in parallel
		relevantTxtFiles.forEach((file) => {
			const reader = new FileReader();
			reader.onload = async (event) => {
				const fileData = event.target?.result as string;
				if (fileData) {
					Logger.info(
						`[MigrationPage] Processing file: ${getBasename(file)}`
					);
					await readTxtFile(fileData, getBasename(file), versionId);
				}
			};
			reader.readAsText(file);
		});

		// Process XML file
		if (xmlFile) {
			processXmlFile(xmlFile);
		}

		// Process image files
		if (imageFiles.length > 0) {
			setImageStats({ total: imageFiles.length, uploaded: 0, failed: 0 });
			const imageMetaMap = imageTxtFile
				? parseImageTxt(await imageTxtFile.text())
				: await fetchImageMetaFromDlMig(organizationId);
			processImageFiles(imageFiles, imageMetaMap);
		}
	};

	// Fall back to DL_MIG when IMAGE.txt isn't in the dropped folder — the user
	// has already uploaded it in a previous migration pass and just wants to
	// drop the Pic folder now.
	const fetchImageMetaFromDlMig = async (
		orgId: string
	): Promise<Map<string, Record<string, string>>> => {
		const map = new Map<string, Record<string, string>>();
		const pageSize = 1000;
		let from = 0;
		while (true) {
			const { data, error } = await supabase
				.from("DL_MIG")
				.select("row_value")
				.eq("organization_id", orgId)
				.eq("table_name", "IMAGE.txt")
				.range(from, from + pageSize - 1);
			if (error) {
				Logger.error(
					error,
					{},
					"[MigrationPage] Failed to fetch IMAGE.txt rows from DL_MIG"
				);
				break;
			}
			if (!data || data.length === 0) break;
			for (const r of data) {
				const row = r.row_value as Record<string, string> | null;
				const bild = row?.["Bild"];
				if (!bild) continue;
				map.set(bild.replace(/[{}]/g, "").toLowerCase(), row!);
			}
			if (data.length < pageSize) break;
			from += pageSize;
		}
		Logger.info(
			`[MigrationPage] Loaded ${map.size} IMAGE.txt rows from DL_MIG`
		);
		return map;
	};

	const processXmlFile = async (file: File) => {
		const reader = new FileReader();
		reader.onload = async (event) => {
			const xmlContent = event.target?.result as string;
			await processXmlContent(xmlContent);
		};
		reader.readAsText(file);
	};

	const extractInfo = (xmlString: string) => {
		try {
			const [a, valueWithRest] = xmlString.split(">", 2);
			const [_, tagInfo] = a.split("<", 2);
			const [value] = valueWithRest.split("<", 1);

			const [groupAndTag, typeInfo, multInfo] = tagInfo.split(" ");
			const [group, tag] = groupAndTag.split(".");
			const type = typeInfo?.split("=")[1]?.replace(/"/g, "");
			const mult = multInfo
				? parseInt(multInfo.split("=")[1].replace(/"/g, ""))
				: null;

			return { group, tag, value, type, mult };
		} catch {
			return {
				group: null,
				tag: null,
				value: null,
				type: null,
				mult: null,
			};
		}
	};

	const processXmlContent = async (xmlContent: string) => {
		if (!organizationId) {
			setXmlError("Organisation nicht gefunden.");
			return;
		}

		const xmlLines = xmlContent.split("\n");
		let organizationData: { [key: string]: any } = {};
		let configData: { [key: string]: any } = {};
		let reminderFee = 0;
		let reminderFeeStage: number | null = null;
		const reminderTitles: Partial<Record<ReminderTemplateType, string>> =
			{};

		for (const line of xmlLines) {
			const { group, tag, value, mult } = extractInfo(line);
			if (!group || !tag || !value) continue;

			configData = {
				...configData,
				[group]: {
					...configData[group],
					[tag]: value,
				},
			};

			if (group === "Rg") {
				if (tag === "MWStisEUSt_OK") {
					organizationData = {
						...organizationData,
						is_mwst_eust: value !== "Falsch",
					};
				} /* else if (tag === "MwStNummer") {
					organizationData = {
						...organizationData,
						mwst_number: value,
					};
				} */ else if (tag === "MWStInkl_OK") {
					organizationData = {
						...organizationData,
						is_mwst_included: value !== "Falsch",
					};
				} /* else if (tag === "Waehrung") {
					organizationData = {
						...organizationData,
						currency_text: value,
					};
				} */ else if (tag === "Mahndauer1") {
					organizationData = {
						...organizationData,
						days_payment_reminder: parseInt(value, 10),
					};
				} else if (tag === "Mahndauer2") {
					organizationData = {
						...organizationData,
						days_payment_mahnung_1: parseInt(value, 10),
					};
				} else if (tag === "Mahndauer3") {
					organizationData = {
						...organizationData,
						days_payment_mahnung_2: parseInt(value, 10),
					};
				} else if (tag === "Zahlungsziel1") {
					organizationData = {
						...organizationData,
						days_payment: parseInt(value, 10),
					};
				} else if (tag === "Zahlungsziel2") {
					organizationData = {
						...organizationData,
						days_payment_akonto: parseInt(value, 10),
					};
				} else if (tag === "MaxMahn") {
					organizationData = {
						...organizationData,
						mahnung_max_count: parseInt(value, 10),
					};
				} else if (tag === "MZuschlag") {
					reminderFee =
						parseFloat(value.replace(",", ".")) / (mult || 1);
				} else if (tag === "MZuschlagAb") {
					reminderFeeStage = parseInt(value, 10);
				}
			} else if (group === "Print" && DLXP_REMINDER_TITLE_KEYS[tag]) {
				for (const templateType of DLXP_REMINDER_TITLE_KEYS[tag]) {
					reminderTitles[templateType] = value;
				}
			} else if (group === "Brief") {
				if (tag === "HeaderGLN") {
					organizationData = {
						...organizationData,
						gln: value,
					};
				} else if (tag === "HeaderAnrede1") {
					organizationData = {
						...organizationData,
						title_or_company_name: value,
					};
				}
			}
		}

		organizationData = {
			...organizationData,
			konfiguration: configData,
		};

		if (
			reminderFee > 0 &&
			reminderFeeStage !== null &&
			reminderFeeStage >= 1 &&
			reminderFeeStage <= 4
		) {
			organizationData.reminder_fee_amounts = {
				[`stage_${Math.max(reminderFeeStage, FIRST_FEE_STAGE)}`]:
					applyRounding(reminderFee),
			};
		}

		if (Object.keys(reminderTitles).length > 0) {
			const { data: currentOrganization, error: configError } =
				await supabase
					.from(SupabaseWriteTableEnum.ORGANIZATIONS)
					.select("documents_config")
					.eq("id", organizationId)
					.single();
			if (configError) {
				setXmlError("Fehler beim Laden der PDF-Einstellungen.");
				return;
			}
			const documentsConfig = (currentOrganization?.documents_config ??
				{}) as unknown as Record<string, Record<string, unknown>>;
			organizationData.documents_config = {
				...documentsConfig,
				...Object.fromEntries(
					Object.entries(reminderTitles).map(
						([templateType, title]) => [
							templateType,
							{ ...documentsConfig[templateType], title },
						]
					)
				),
			};
		}

		const { error } = await supabase
			.from(SupabaseWriteTableEnum.ORGANIZATIONS)
			.update(organizationData)
			.eq("id", organizationId);

		if (error) {
			setXmlError("Fehler beim Speichern der Organisation.");
		} else {
			setXmlProcessed(true);
		}
	};

	const processImageFiles = async (
		files: File[],
		imageMetaMap: Map<string, Record<string, string>>
	) => {
		if (!organizationId) {
			setImageStats((prev) => ({
				...prev,
				failed: files.length,
			}));
			return;
		}

		// Storage uploads are per-file (no bulk endpoint), but files-row
		// inserts are batched to keep total request volume comparable to the
		// txt → DL_MIG path. Workers push successful rows to fileRowBuffer;
		// flushFileRowBuffer drains it in chunks, gated by a mutex so two
		// flushes can't race.
		const fileRowBuffer: Array<Partial<FileEntityType>> = [];
		const FLUSH_THRESHOLD = 200;
		let flushing = false;
		const flushFileRowBuffer = async (force: boolean): Promise<void> => {
			if (flushing) return;
			if (!force && fileRowBuffer.length < FLUSH_THRESHOLD) return;
			flushing = true;
			try {
				while (fileRowBuffer.length >= (force ? 1 : FLUSH_THRESHOLD)) {
					const chunk = fileRowBuffer.splice(0, FLUSH_THRESHOLD);
					const { error } = await supabase
						.from("files")
						.insert(chunk as FileEntityType[]);
					if (error) {
						Logger.error(
							error,
							{},
							`[MigrationPage] Batch files insert failed (${chunk.length} rows)`
						);
					}
				}
			} finally {
				flushing = false;
			}
		};

		const uploadOne = async (file: File): Promise<void> => {
			const m = file.name.match(/\{([0-9A-Fa-f-]+)\}/);
			if (!m) {
				Logger.warn(
					`[MigrationPage] Image filename has no UUID, skipping: ${file.name}`
				);
				setImageStats((prev) => ({
					...prev,
					failed: prev.failed + 1,
				}));
				return;
			}
			const uuid = m[1];
			const fileName = file.name.replace(/[{}]/g, "");
			const filePath = `${organizationId}/pic/${uuid.toLowerCase()}/${fileName}`;
			const meta = imageMetaMap.get(fileName.toLowerCase());
			try {
				// No upsert: the v1 bucket only has an INSERT policy, so
				// upsert escalates to UPDATE and fails RLS. Plain INSERT and
				// treat "already exists" as success so reruns are idempotent.
				const { error: uploadError } = await supabase.storage
					.from("v1")
					.upload(filePath, file);
				const alreadyExists =
					uploadError &&
					((uploadError as any).statusCode === "409" ||
						/already exists|duplicate/i.test(
							uploadError.message ?? ""
						));
				if (uploadError && !alreadyExists) {
					Logger.error(
						uploadError,
						{},
						`[MigrationPage] Image upload failed: ${file.name}`
					);
					setImageStats((prev) => ({
						...prev,
						failed: prev.failed + 1,
					}));
					return;
				}

				// Queue a "blanko" files row with job_id NULL. The
				// process_row Faelle.txt branch backfills job_id later by
				// matching meta_data->>'IMA_FAEPt' to the new job's FAE_ID.
				// created_at/modified_at must always be present: PostgREST
				// batch inserts derive the column set from the union of keys
				// in the array, so omitting them in some rows sends explicit
				// NULL (which violates NOT NULL on created_at).
				const nowIso = new Date().toISOString();
				fileRowBuffer.push({
					organization_id: organizationId,
					job_id: null,
					file_name: fileName,
					path_name: filePath,
					bucket_name: "v1",
					meta_data: meta ?? {},
					created_at: parseDlxpTimestamp(meta?.IMA_EDatum) ?? nowIso,
					modified_at: parseDlxpTimestamp(meta?.IMA_MDatum) ?? nowIso,
				});

				setImageStats((prev) => ({
					...prev,
					uploaded: prev.uploaded + 1,
				}));

				// Opportunistic flush — non-blocking, mutex inside.
				flushFileRowBuffer(false);
			} catch (err) {
				Logger.error(
					err,
					{},
					`[MigrationPage] Image upload threw: ${file.name}`
				);
				setImageStats((prev) => ({
					...prev,
					failed: prev.failed + 1,
				}));
			}
		};

		// Bypass the client-side rate-limit guard (lib/supabase/supabaseClient
		// caps at 800 req/min and hard-blocks the page). Bulk image migration
		// legitimately exceeds that. Always restore in finally{} so a thrown
		// upload never leaves the guard disabled.
		setRateLimitBypass(true);
		try {
			const concurrency = 8;
			let cursor = 0;
			const workers = Array.from({ length: concurrency }, async () => {
				while (cursor < files.length) {
					const idx = cursor++;
					await uploadOne(files[idx]);
				}
			});
			await Promise.all(workers);
			await flushFileRowBuffer(true);
		} finally {
			setRateLimitBypass(false);
		}
	};

	// Calculate progress stats
	const getProgressStats = () => {
		let totalProcessed = 0;
		let totalRows = 0;

		filesFound.forEach((fileName) => {
			const fileProgress = progress[fileName];
			if (fileProgress) {
				totalProcessed +=
					fileProgress.processedSuccess + fileProgress.processedError;
				totalRows += fileProgress.totalLength;
			}
		});

		// Also count image binary uploads so the top-banner progress reflects
		// a Pic-folder-only run (no txt files in the drop).
		totalProcessed += imageStats.uploaded + imageStats.failed;
		totalRows += imageStats.total;

		return { processed: totalProcessed, total: totalRows };
	};

	const stats = getProgressStats();

	const handleDownloadExportFile = async () => {
		try {
			const { data, error } = await supabase.storage
				.from("dD_files")
				.download("DLXP_Export.BAS");

			if (error) throw error;

			// Create a download link
			const url = window.URL.createObjectURL(data);
			const a = document.createElement("a");
			a.href = url;
			a.download = "DLXP_Export.BAS";
			document.body.appendChild(a);
			a.click();
			window.URL.revokeObjectURL(url);
			document.body.removeChild(a);

			showNotification({
				message: "Die Export-Datei wurde heruntergeladen",
				type: "success",
			});
		} catch (error) {
			Logger.error("Error downloading export file: " + error);
			showNotification({
				message: "Die Export-Datei konnte nicht heruntergeladen werden",
				type: "error",
			});
		}
	};

	return (
		<div className="container mx-auto px-6 py-8 max-w-7xl">
			{/* Status Banner */}
			{uploadStatus !== "idle" && (
				<Card
					className={clsx("mb-6 sticky top-4 z-50 border-2", {
						"border-yellow-500 bg-yellow-50":
							uploadStatus === "uploading",
						"border-green-500 bg-green-50":
							uploadStatus === "complete",
						"border-destructive bg-destructive/10":
							uploadStatus === "error",
					})}
				>
					<CardContent>
						<div className="flex items-center gap-4">
							<div className="flex-1">
								{uploadStatus === "uploading" && (
									<div className="space-y-2">
										<div className="flex items-center gap-2">
											<AlertCircle className="h-6 w-6 text-yellow-600" />
											<h3 className="text-lg font-bold text-yellow-900">
												Upload läuft - Bitte Seite nicht
												schließen!
											</h3>
										</div>
										<p className="text-sm text-yellow-800">
											{stats.processed} von {stats.total}{" "}
											Zeilen verarbeitet (
											{filesFound.length} Dateien)
										</p>
										{stats.total > 0 && (
											<Progress
												value={
													(stats.processed /
														stats.total) *
													100
												}
												className="h-2"
											/>
										)}
									</div>
								)}
								{uploadStatus === "complete" && (
									<div className="space-y-3">
										<div className="flex items-center gap-2">
											<CheckCircle2 className="h-6 w-6 text-green-600" />
											<h3 className="text-lg font-bold text-green-900">
												Upload erfolgreich abgeschlossen
											</h3>
										</div>
										{(() => {
											const emptyFiles =
												filesFound.filter(
													(file) =>
														progress[file]
															?.totalLength ===
															0 &&
														EXPECTED_FILES.includes(
															file
														)
												);
											if (emptyFiles.length > 0) {
												return (
													<div className="p-3 bg-amber-50 border border-amber-200 rounded-md">
														<div className="flex items-center gap-2 mb-2">
															<AlertCircle className="h-4 w-4 text-amber-600" />
															<p className="text-sm font-semibold text-amber-900">
																Wichtige leere
																Dateien (0
																Einträge)
															</p>
														</div>
														<ul className="list-disc list-inside text-sm text-amber-800 ml-1 columns-2 md:columns-3 gap-4">
															{emptyFiles.map(
																(file) => (
																	<li
																		key={
																			file
																		}
																		className="break-inside-avoid"
																	>
																		{file}
																	</li>
																)
															)}
														</ul>
														<p className="text-xs text-amber-700 mt-2">
															Diese Dateien
															sollten
															normalerweise Daten
															enthalten. Bitte
															überprüfen Sie, ob
															der Export korrekt
															durchgeführt wurde.
														</p>
													</div>
												);
											}
											return null;
										})()}
										<p className="text-sm text-muted-foreground">
											Sende uns bei Fragen eine
											Support-Nachricht oder ruf an unter{" "}
											<strong>+41 31 561 93 27</strong>.
										</p>
										<button
											onClick={handleReset}
											className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md font-medium transition-colors"
										>
											Neuen Import starten
										</button>
									</div>
								)}
								{uploadStatus === "error" && (
									<div className="space-y-2">
										<div className="flex items-center gap-2">
											<XCircle className="h-6 w-6 text-destructive" />
											<h3 className="text-lg font-bold text-destructive">
												Upload mit Fehlern abgeschlossen
											</h3>
										</div>
										<p className="text-sm text-muted-foreground">
											Bei Fragen senden Sie uns eine
											Support-Nachricht oder rufen Sie uns
											an unter{" "}
											<strong>+41 31 561 93 27</strong>.
										</p>
										<button
											onClick={handleReset}
											className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md font-medium transition-colors"
										>
											Neuen Import starten
										</button>
									</div>
								)}
							</div>
						</div>
					</CardContent>
				</Card>
			)}

			{/* Skip Migration Card - only show if no decision made or chose to migrate */}
			{(!migrationDecision ||
				migrationDecision === MIGRATION_DECISION.MIGRATE_FROM_DLXP ||
				migrationDecision === ONBOARDING_STATUS.OPEN) && (
				<Card className="mb-6">
					<CardContent>
						<div className="flex items-center gap-4">
							<Info className="h-6 w-6 text-blue-500" />
							<div className="flex-1">
								<p className="text-lg font-semibold">
									Sie sind noch nicht fertig mit dem
									Onboarding.
								</p>
							</div>
							<Button
								onClick={handleSkipMigration}
								variant="outline"
							>
								Migration auslassen
							</Button>
						</div>
					</CardContent>
				</Card>
			)}

			<div className="mb-6">
				<h1 className="text-3xl font-bold mb-2">
					Dental lab XP Import
				</h1>
				<p className="text-muted-foreground">
					Importieren Sie Ihre Daten aus Dental lab XP in dentLAB.
				</p>
			</div>

			<Accordion
				type="multiple"
				value={openAccordion}
				onValueChange={setOpenAccordion}
				className="space-y-4"
			>
				<AccordionItem value="step-1" className="border rounded-lg">
					<Card className="border-0">
						<AccordionTrigger className="px-6 hover:no-underline">
							<div className="flex items-center gap-3 text-left">
								<div className="flex items-center justify-center w-8 h-8 rounded-full bg-blue-100 text-blue-600 font-semibold flex-shrink-0">
									1
								</div>
								<div className="flex-1">
									<h2 className="text-lg font-semibold">
										Vorbereitung
									</h2>
									<p className="text-sm text-muted-foreground font-normal">
										Stellen Sie sicher, dass Dental lab XP
										auf dem neuesten Stand ist und erstellen
										Sie ein Backup. Dies gewährleistet einen
										reibungslosen Export und schützt Ihre
										Daten.
									</p>
								</div>
							</div>
						</AccordionTrigger>
						<AccordionContent>
							<CardContent className="pt-0 pb-6">
								<div className="space-y-6 max-w-3xl mx-auto">
									{/* Step 1.1 */}
									<div className="flex flex-col items-center">
										<div className="bg-blue-50 border-2 border-blue-500 rounded-lg p-4 w-full">
											<div className="flex items-center gap-3 mb-3">
												<div className="flex items-center justify-center w-8 h-8 rounded-full bg-blue-500 text-white font-bold flex-shrink-0">
													1.1
												</div>
												<p className="font-semibold text-gray-900">
													Öffnen Sie das Programm
													"DLXPUtil"
												</p>
											</div>
											<div className="rounded border border-gray-300 overflow-hidden bg-white">
												<img
													src="/images/migration/dlxputil-search.png"
													alt="DLXPUtil suchen"
													className="w-full h-auto"
												/>
											</div>
										</div>
										<ArrowDown className="h-8 w-8 text-blue-500 my-2" />
									</div>

									{/* Step 1.2 */}
									<div className="flex flex-col items-center">
										<div className="bg-blue-50 border-2 border-blue-500 rounded-lg p-4 w-full">
											<div className="flex items-center gap-3 mb-3">
												<div className="flex items-center justify-center w-8 h-8 rounded-full bg-blue-500 text-white font-bold flex-shrink-0">
													1.2
												</div>
												<p className="font-semibold text-gray-900">
													Klicken Sie auf "Update"
												</p>
											</div>
											<div className="rounded border border-gray-300 overflow-hidden bg-white">
												<img
													src="/images/migration/dlxputil-update.png"
													alt="DLXPUtil Update"
													className="w-full h-auto"
												/>
											</div>
										</div>
										<ArrowDown className="h-8 w-8 text-blue-500 my-2" />
									</div>

									{/* Step 1.3 */}
									<div className="flex flex-col items-center">
										<div className="bg-blue-50 border-2 border-blue-500 rounded-lg p-4 w-full">
											<div className="flex items-center gap-3 mb-3">
												<div className="flex items-center justify-center w-8 h-8 rounded-full bg-blue-500 text-white font-bold flex-shrink-0">
													1.3
												</div>
												<p className="font-semibold text-gray-900">
													Wählen Sie die Update-Option
													aus und führen Sie das
													Update durch
												</p>
											</div>
											<div className="rounded border border-gray-300 overflow-hidden bg-white">
												<img
													src="/images/migration/select-update-option.png"
													alt="Update Option auswählen"
													className="w-full h-auto"
												/>
											</div>
										</div>
										<ArrowDown className="h-8 w-8 text-blue-500 my-2" />
									</div>

									{/* Info box between 1.3 and 1.4 */}
									<div className="flex flex-col items-center">
										<div className="bg-green-50 border-2 border-green-500 rounded-lg p-4 w-full">
											<p className="text-sm text-green-900 mb-2 font-semibold">
												Update erfolgreich
												abgeschlossen? Schliessen Sie
												nun DLXPUtil und öffnen Sie
												Dental lab XP.
											</p>
											<p className="text-sm text-green-900 font-semibold">
												Fahren Sie anschliessend mit der
												Erstellung eines Backups fort,
												um Ihre Daten zu sichern.
											</p>
										</div>
										<ArrowDown className="h-8 w-8 text-green-500 my-2" />
									</div>

									{/* Step 1.4 */}
									<div className="flex flex-col items-center">
										<div className="bg-blue-50 border-2 border-blue-500 rounded-lg p-4 w-full">
											<div className="flex items-center gap-3 mb-3">
												<div className="flex items-center justify-center w-8 h-8 rounded-full bg-blue-500 text-white font-bold flex-shrink-0">
													1.4
												</div>
												<p className="font-semibold text-gray-900">
													Finden Sie die
													Backup-Funktion in Dental
													lab XP
												</p>
											</div>
											<div className="rounded border border-gray-300 overflow-hidden bg-white">
												<img
													src="/images/migration/find-backup-dlxp.png"
													alt="Backup in Dental lab XP finden"
													className="w-full h-auto"
												/>
											</div>
										</div>
										<ArrowDown className="h-8 w-8 text-blue-500 my-2" />
									</div>

									{/* Step 1.5 */}
									<div className="flex flex-col items-center">
										<div className="bg-blue-50 border-2 border-blue-500 rounded-lg p-4 w-full">
											<div className="flex items-center gap-3 mb-3">
												<div className="flex items-center justify-center w-8 h-8 rounded-full bg-blue-500 text-white font-bold flex-shrink-0">
													1.5
												</div>
												<p className="font-semibold text-gray-900">
													Wählen Sie eine Art des
													Backups aus und erstellen es
												</p>
											</div>
											<div className="rounded border border-gray-300 overflow-hidden bg-white">
												<img
													src="/images/migration/decide-backup-type.png"
													alt="Backup-Typ auswählen"
													className="w-full h-auto"
												/>
											</div>
										</div>
									</div>
									<div className="pt-4 flex justify-end">
										<button
											onClick={() =>
												setOpenAccordion(["step-2"])
											}
											className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md font-medium transition-colors flex items-center gap-2"
										>
											Weiter zu Schritt 2
											<ChevronRight className="h-4 w-4" />
										</button>
									</div>
								</div>
							</CardContent>
						</AccordionContent>
					</Card>
				</AccordionItem>

				<AccordionItem value="step-2" className="border rounded-lg">
					<Card className="border-0">
						<AccordionTrigger className="px-6 hover:no-underline">
							<div className="flex items-center gap-3 text-left">
								<div className="flex items-center justify-center w-8 h-8 rounded-full bg-blue-100 text-blue-600 font-semibold flex-shrink-0">
									2
								</div>
								<div className="flex-1">
									<h2 className="text-lg font-semibold">
										Export aus Dental lab XP
									</h2>
									<p className="text-sm text-muted-foreground font-normal">
										Extrahieren Sie alle relevanten Daten
										aus Dental lab XP in ein für dentLAB
										lesbares Format. Dieser Schritt bereitet
										Ihre Daten für den Import vor.
									</p>
								</div>
							</div>
						</AccordionTrigger>
						<AccordionContent>
							<CardContent className="pt-0 pb-6">
								<div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-md space-y-2">
									<p className="text-sm text-amber-900">
										<strong>Wichtiger Hinweis:</strong>{" "}
										Während des Exports darf Dental lab XP
										nicht verwendet werden. Die Verwendung
										des Programms während des Exports führt
										zum Fehlschlagen des Exports.
									</p>
									<p className="text-sm text-amber-900">
										Je nach Datenmenge (Laborgrösse und
										Nutzungsdauer) kann der Export mehrere
										Stunden dauern. Es empfiehlt sich daher,
										den Export z.B. abends durchzuführen,
										wenn das Labor geschlossen ist.
									</p>
								</div>
								<div className="space-y-6 max-w-3xl mx-auto">
									{/* Step 2.1 */}
									<div className="flex flex-col items-center">
										<div className="bg-blue-50 border-2 border-blue-500 rounded-lg p-4 w-full">
											<div className="flex items-center gap-3 mb-3">
												<div className="flex items-center justify-center w-8 h-8 rounded-full bg-blue-500 text-white font-bold flex-shrink-0">
													2.1
												</div>
												<p className="font-semibold text-gray-900">
													Laden Sie das Exportskript
													herunter
												</p>
											</div>
											<button
												onClick={
													handleDownloadExportFile
												}
												className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md font-medium transition-colors"
												aria-label="Export-Datei herunterladen"
											>
												DLXP_Export.BAS herunterladen
											</button>
										</div>
										<ArrowDown className="h-8 w-8 text-blue-500 my-2" />
									</div>

									{/* Step 2.2 */}
									<div className="flex flex-col items-center">
										<div className="bg-blue-50 border-2 border-blue-500 rounded-lg p-4 w-full">
											<div className="flex items-center gap-3 mb-3">
												<div className="flex items-center justify-center w-8 h-8 rounded-full bg-blue-500 text-white font-bold flex-shrink-0">
													2.2
												</div>
												<p className="font-semibold text-gray-900">
													Unten in der Ecke finden Sie
													die Funktion{" "}
													<strong>
														"Makro ausführen"
													</strong>
													, wählen Sie{" "}
													<strong>
														"DLXP_Export.BAS"
													</strong>{" "}
													aus Ihren Downloads
												</p>
											</div>
											<div className="rounded border border-gray-300 overflow-hidden bg-white">
												<img
													src="/images/migration/run-macro.png"
													alt="LOKAL → SpotNotes Menu"
													className="w-full h-auto"
												/>
											</div>
										</div>
										<ArrowDown className="h-8 w-8 text-blue-500 my-2" />
									</div>

									{/* Step 2.3 */}
									<div className="flex flex-col items-center">
										<div className="bg-blue-50 border-2 border-blue-500 rounded-lg p-4 w-full">
											<div className="flex items-center gap-3 mb-3">
												<div className="flex items-center justify-center w-8 h-8 rounded-full bg-blue-500 text-white font-bold flex-shrink-0">
													2.3
												</div>
												<p className="font-semibold text-gray-900">
													Das Exportskript wird
													ausgeführt (Status am
													unteren linken Rand)
												</p>
											</div>
											<div className="rounded border border-gray-300 overflow-hidden bg-white">
												<img
													src="/images/migration/process-status.png"
													alt="Export Status"
													className="w-full h-auto"
												/>
											</div>
										</div>
										<ArrowDown className="h-8 w-8 text-blue-500 my-2" />
									</div>

									{/* Step 2.4 */}
									<div className="flex flex-col items-center">
										<div className="bg-blue-50 border-2 border-blue-500 rounded-lg p-4 w-full">
											<div className="flex items-center gap-3 mb-3">
												<div className="flex items-center justify-center w-8 h-8 rounded-full bg-blue-500 text-white font-bold flex-shrink-0">
													2.4
												</div>
												<p className="font-semibold text-gray-900">
													Warten Sie auf die
													Erfolgsmeldung
												</p>
											</div>
											<div className="rounded border border-gray-300 overflow-hidden bg-white">
												<img
													src="/images/migration/success-message.png"
													alt="Export erfolgreich abgeschlossen"
													className="w-full h-auto"
												/>
											</div>
										</div>
										<ArrowDown className="h-8 w-8 text-blue-500 my-2" />
									</div>

									{/* Step 2.5 */}
									<div className="flex flex-col items-center">
										<div className="bg-blue-50 border-2 border-blue-500 rounded-lg p-4 w-full">
											<div className="flex items-center gap-3 mb-3">
												<div className="flex items-center justify-center w-8 h-8 rounded-full bg-blue-500 text-white font-bold flex-shrink-0">
													2.5
												</div>
												<p className="font-semibold text-gray-900">
													Finden Sie das Verzeichnis{" "}
													<strong>"DLCloud"</strong>{" "}
													unter{" "}
													<code className="bg-blue-100 px-1 py-0.5 rounded">
														C:\_DLXPData\
													</code>
												</p>
											</div>
											<p className="text-sm text-gray-700 mb-3">
												Das Verzeichnis{" "}
												<strong>_DLXPData</strong>{" "}
												befindet sich normalerweise auf
												der Partition bzw. dem Laufwerk,
												auf dem Dental lab XP
												installiert ist. Dies kann auch
												ein Server sein.
											</p>
											<div className="space-y-2">
												<div className="rounded border border-gray-300 overflow-hidden bg-white">
													<img
														src="/images/migration/find-dlxpdata.png"
														alt="_DLXPData Ordner finden"
														className="w-full h-auto"
													/>
												</div>
												<div className="rounded border border-gray-300 overflow-hidden bg-white">
													<img
														src="/images/migration/find-dlcloud.png"
														alt="DLCloud Ordner im Explorer"
														className="w-full h-auto"
													/>
												</div>
											</div>
										</div>
									</div>
									<div className="pt-4 flex justify-end">
										<button
											onClick={() =>
												setOpenAccordion(["step-3"])
											}
											className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md font-medium transition-colors flex items-center gap-2"
										>
											Weiter zu Schritt 3
											<ChevronRight className="h-4 w-4" />
										</button>
									</div>
								</div>
							</CardContent>
						</AccordionContent>
					</Card>
				</AccordionItem>

				<AccordionItem value="step-3" className="border rounded-lg">
					<Card className="border-0">
						<AccordionTrigger className="px-6 hover:no-underline">
							<div className="flex items-center gap-3 text-left">
								<div className="flex items-center justify-center w-8 h-8 rounded-full bg-blue-100 text-blue-600 font-semibold flex-shrink-0">
									3
								</div>
								<div className="flex-1">
									<h2 className="text-lg font-semibold">
										Import in dentLAB
									</h2>
									<p className="text-sm text-muted-foreground font-normal">
										Laden Sie die exportierten Daten hoch.
										Unser Team überspielt die Daten
										anschliessend auf Ihr Profil und
										informiert Sie, sobald Ihre Kunden,
										Aufträge und Einstellungen in dentLAB
										verfügbar sind.
									</p>
								</div>
							</div>
						</AccordionTrigger>
						<AccordionContent>
							<CardContent className="pt-0 pb-6">
								<div className="space-y-4">
									<div className="flex items-center space-x-2">
										<Checkbox
											id="include-images"
											checked={includeImages}
											onCheckedChange={(checked) =>
												setIncludeImages(
													checked === true
												)
											}
											disabled={
												uploadStatus === "uploading" ||
												uploadStatus === "complete" ||
												uploadStatus === "error"
											}
											aria-label="Bild-Dateien einbeziehen"
											data-testid="include-images-checkbox"
										/>
										<Label
											htmlFor="include-images"
											className={
												uploadStatus !== "idle"
													? "cursor-not-allowed opacity-50 flex items-center gap-2"
													: "cursor-pointer flex items-center gap-2"
											}
										>
											<ImageIcon className="h-4 w-4" />
											Bild-Dateien mit importieren
											(optional)
										</Label>
									</div>

									<TxtFilesMigration
										onUpload={handleFolderUpload}
										filesFound={filesFound}
										disabled={
											uploadStatus === "uploading" ||
											uploadStatus === "complete" ||
											uploadStatus === "error"
										}
									/>
								</div>
							</CardContent>
						</AccordionContent>
					</Card>
				</AccordionItem>
			</Accordion>

			{(filesFound.length > 0 || imageStats.total > 0) && (
				<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mt-6">
					{filesFound
						.filter((key) => progress[key]?.totalLength > 0)
						.map((key) => {
							const processed =
								progress[key].processedSuccess +
								progress[key].processedError;
							const progressValue =
								(processed / progress[key].totalLength) * 100 ||
								0;
							const isComplete =
								processed === progress[key].totalLength;
							const hasErrors = progress[key].processedError > 0;

							return (
								<Card
									key={key}
									className={clsx({
										"border-destructive": hasErrors,
										"border-green-500":
											isComplete && !hasErrors,
									})}
								>
									<CardHeader>
										<CardTitle className="text-base flex items-center justify-between">
											<span className="truncate">
												{key}
											</span>
											{isComplete && !hasErrors && (
												<CheckCircle2 className="h-4 w-4 text-green-600 flex-shrink-0" />
											)}
											{hasErrors && (
												<XCircle className="h-4 w-4 text-destructive flex-shrink-0" />
											)}
										</CardTitle>
									</CardHeader>
									<CardContent>
										<div className="space-y-2">
											<Progress value={progressValue} />
											<div className="flex items-center justify-between text-sm text-muted-foreground">
												<span>
													{processed}/
													{progress[key].totalLength}
												</span>
												<span>
													{Math.round(progressValue)}%
												</span>
											</div>
											{hasErrors && (
												<Badge
													variant="destructive"
													className="text-xs"
												>
													{
														progress[key]
															.processedError
													}{" "}
													Fehler
												</Badge>
											)}
										</div>
									</CardContent>
								</Card>
							);
						})}

					{(xmlProcessed || xmlError) && (
						<Card
							className={clsx({
								"border-destructive": !!xmlError,
								"border-green-500": xmlProcessed && !xmlError,
							})}
						>
							<CardHeader>
								<CardTitle className="text-base flex items-center justify-between">
									<span className="truncate">
										Konfiguration.xml
									</span>
									{xmlProcessed && !xmlError && (
										<CheckCircle2 className="h-4 w-4 text-green-600 flex-shrink-0" />
									)}
									{xmlError && (
										<XCircle className="h-4 w-4 text-destructive flex-shrink-0" />
									)}
								</CardTitle>
							</CardHeader>
							<CardContent>
								{xmlError ? (
									<p className="text-sm text-destructive">
										{xmlError}
									</p>
								) : (
									<p className="text-sm text-green-600">
										XML Konfiguration erfolgreich
										verarbeitet
									</p>
								)}
							</CardContent>
						</Card>
					)}

					{includeImages && imageStats.total > 0 && (
						<Card>
							<CardHeader>
								<CardTitle className="text-base flex items-center justify-between">
									<span className="truncate">Bilder</span>
									{imageStats.uploaded === imageStats.total &&
										imageStats.failed === 0 && (
											<CheckCircle2 className="h-4 w-4 text-green-600 flex-shrink-0" />
										)}
									{imageStats.failed > 0 && (
										<XCircle className="h-4 w-4 text-destructive flex-shrink-0" />
									)}
								</CardTitle>
							</CardHeader>
							<CardContent>
								<div className="space-y-2">
									<Progress
										value={
											((imageStats.uploaded +
												imageStats.failed) /
												imageStats.total) *
											100
										}
									/>
									<div className="flex items-center justify-between text-sm text-muted-foreground">
										<span>
											{imageStats.uploaded +
												imageStats.failed}{" "}
											/ {imageStats.total}
										</span>
										<span>
											{Math.round(
												((imageStats.uploaded +
													imageStats.failed) /
													imageStats.total) *
													100
											)}
											%
										</span>
									</div>
									{imageStats.failed > 0 && (
										<Badge
											variant="destructive"
											className="text-xs"
										>
											{imageStats.failed} Fehler
										</Badge>
									)}
								</div>
							</CardContent>
						</Card>
					)}

					{(() => {
						const missingFiles = EXPECTED_FILES.filter(
							(file) => !filesFound.includes(file)
						);
						if (missingFiles.length > 0) {
							return (
								<Card className="border-destructive bg-destructive/10">
									<CardHeader>
										<CardTitle className="text-base flex items-center gap-2 text-destructive">
											<XCircle className="h-4 w-4 flex-shrink-0" />
											<span className="truncate">
												Fehlende Dateien
											</span>
										</CardTitle>
									</CardHeader>
									<CardContent>
										<p className="text-sm text-destructive mb-2">
											Folgende erwartete Dateien wurden
											nicht gefunden:
										</p>
										<ul className="list-disc list-inside space-y-1 text-sm text-destructive">
											{missingFiles.map((file) => (
												<li key={file}>{file}</li>
											))}
										</ul>
										<p className="text-xs text-muted-foreground mt-3">
											Bitte überprüfen Sie, ob der Export
											korrekt durchgeführt wurde.
										</p>
									</CardContent>
								</Card>
							);
						}
						return null;
					})()}
				</div>
			)}

			{Object.keys(rowErrors).length > 0 && (
				<Card className="mt-6 border-destructive">
					<CardHeader>
						<CardTitle className="flex items-center gap-2">
							<AlertCircle className="h-5 w-5" />
							Fehlerhafte Zeilen
						</CardTitle>
					</CardHeader>
					<CardContent>
						<div className="space-y-4">
							{Object.keys(rowErrors).map((key) => (
								<div key={key} className="space-y-2">
									<h3 className="font-semibold text-sm">
										{key}
									</h3>
									<div className="space-y-1 max-h-60 overflow-y-auto">
										{rowErrors[key].map((row, index) => (
											<div
												key={index}
												className="text-xs bg-muted p-2 rounded font-mono"
											>
												{JSON.stringify(row.row_value)}
											</div>
										))}
									</div>
								</div>
							))}
						</div>
					</CardContent>
				</Card>
			)}
		</div>
	);
};

const TxtFilesMigration: React.FC<{
	onUpload: (files: File[]) => void;
	filesFound: string[];
	disabled?: boolean;
}> = ({ onUpload, filesFound, disabled = false }) => {
	const fileInputRef = React.useRef<HTMLInputElement>(null);

	const handleFileChange = async (
		event: React.ChangeEvent<HTMLInputElement>
	) => {
		const files = event.target.files;
		if (!files || files.length === 0) return;

		const fileArray = Array.from(files);
		onUpload(fileArray);

		// Clear the file input
		if (fileInputRef.current) {
			fileInputRef.current.value = "";
		}
	};

	const handleFileUpload = async (droppedFiles: File[]) => {
		onUpload(droppedFiles);
	};

	const { isDragging, isUploading, dragHandlers } = useFileUpload({
		onFileUpload: handleFileUpload,
		enableFolderUpload: true,
	});

	return (
		<div>
			<input
				type="file"
				ref={fileInputRef}
				onChange={handleFileChange}
				className="hidden"
				multiple
				{...{ webkitdirectory: "true", directory: "true" }}
			/>

			<div
				{...(disabled ? {} : dragHandlers)}
				className={clsx(
					"border-2 border-dashed rounded-lg p-12 transition-all",
					disabled
						? "cursor-not-allowed opacity-50 border-muted-foreground/25 bg-muted/20"
						: "cursor-pointer",
					!disabled && isDragging
						? "border-primary bg-primary/5"
						: !disabled &&
								"border-muted-foreground/25 hover:border-muted-foreground/50 hover:bg-muted/50"
				)}
				onClick={() => !disabled && fileInputRef.current?.click()}
			>
				{isDragging ? (
					<div className="flex flex-col items-center justify-center gap-2">
						<Upload className="h-10 w-10 text-primary" />
						<p className="text-primary font-medium">
							Ordner hier ablegen
						</p>
					</div>
				) : isUploading ? (
					<div className="flex flex-col items-center justify-center gap-2">
						<div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary"></div>
						<p className="text-muted-foreground">
							Dateien werden verarbeitet...
						</p>
					</div>
				) : (
					<div className="flex flex-col items-center justify-center gap-3">
						<Upload className="h-10 w-10 text-muted-foreground" />
						<div className="text-center">
							<p className="font-medium mb-1">
								Migrationsordner auswählen
							</p>
							<p className="text-sm text-muted-foreground">
								oder per Drag & Drop ablegen
							</p>
						</div>
					</div>
				)}
			</div>

			{filesFound.length > 0 && (
				<div className="mt-4">
					<div className="flex items-center gap-2 text-sm">
						<CheckCircle2 className="h-4 w-4 text-green-600" />
						<span className="font-medium text-green-600">
							{filesFound.length} TXT-Dateien gefunden
						</span>
					</div>
				</div>
			)}
		</div>
	);
};
